import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { fetchAllowedDepartements } from '../reduxStore/ScopeSlice';
import { setSelectedDepartement } from '../reduxStore/AppSlice';

/**
 * Résout dynamiquement si l'utilisateur connecté est "mono-département" — un
 * seul département autorisé (`ScopeSlice.allowedDepartements`), qu'il s'agisse
 * d'un tenant qui n'a qu'un seul département, ou d'un user restreint à un seul
 * parmi plusieurs. Dans ce cas, auto-sélectionne ce département (saute
 * DepartementSelectionPage) — "ce département" et "Tous les départements"
 * représentent exactement les mêmes données avec un seul département.
 *
 * Réévalué à CHAQUE boot authentifié (pas de cache permanent) : si un admin
 * ajoute un département plus tard, le picker réapparaît au prochain login.
 */
export function useDepartementBootstrap() {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((s: RootState) => s.AuthSlice);
  const { selectedDepartement, isAllMode } = useSelector((s: RootState) => s.AppSlice);
  const allowedDepartements = useSelector((s: RootState) => s.ScopeSlice.allowedDepartements);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setResolved(false);
      return;
    }
    dispatch(fetchAllowedDepartements())
      .unwrap()
      .catch(() => {})
      .finally(() => setResolved(true));
  }, [isAuthenticated, dispatch]);

  const isMonoDepartement = resolved && allowedDepartements.length === 1;

  // Auto-sélection : uniquement tant qu'aucun contexte n'est déjà choisi (ne
  // doit jamais écraser un choix explicite de l'utilisateur, ex: mode ALL).
  useEffect(() => {
    if (isMonoDepartement && !selectedDepartement && !isAllMode) {
      dispatch(setSelectedDepartement(allowedDepartements[0]));
    }
  }, [isMonoDepartement, selectedDepartement, isAllMode, allowedDepartements, dispatch]);

  return { resolved, isMonoDepartement };
}
